import { isEmpty } from 'lodash-es';
import { useCallback, useMemo, useState } from 'react';
import { MultiSectionsDisplay } from 'src/components/Events/VenueMap';
import { Content } from 'src/contexts/ContentContext';
import { useSiteTheme } from 'src/contexts/SiteTheme/SiteThemeContext';
import { vars } from 'src/core/themes';
import { Button, Stack } from 'src/core/ui';
import { CrossIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import { SectionInfo, SectionSectionGroup } from 'src/WebApiController';

import { ClickToExpand } from './ClickToExpand';
import { ModifySectionGroup } from './ModifySectionGroup';
import {
  DefaultSectionGroup,
  SectionSectionGroupWithId,
} from './SectionGroupPanelV2.hooks';
import { toSectionGroupKey } from './SectionGroupPanelV2.utils';

type ZoneMapSelectedSectionsActionsProps = {
  selectedSections: SectionInfo[];
  zonedSectionIds: Set<number>;
  sectionGroups: SectionSectionGroupWithId[];
  onResetSections: () => void;
  onUpdateZoneGroup: () => void;
  onUnassignSections: (sectionIds: number[]) => void;
  onCreateSectionGroup: (
    sectionIds: number[],
    sectionGroupAreaName: string,
    sectionGroupColor: string
  ) => void;
};

export const ZoneMapSelectedSectionsActions = ({
  selectedSections,
  zonedSectionIds,
  sectionGroups,
  onResetSections,
  onUpdateZoneGroup,
  onUnassignSections,
  onCreateSectionGroup,
}: ZoneMapSelectedSectionsActionsProps) => {
  const [open, setOpen] = useState(false);

  const { isDarkMode } = useSiteTheme();
  const selectedSectionIds = useMemo(
    () => selectedSections.map(({ id }) => id),
    [selectedSections]
  );

  const existingGroupIds = useMemo(
    () => sectionGroups.map(({ id }) => id),
    [sectionGroups]
  );

  const onSaveSectionGroup = useCallback(
    (sectionGroup: SectionSectionGroup) => {
      if (
        sectionGroup.sectionGroupAreaName &&
        sectionGroup.sectionGroupColor &&
        !existingGroupIds.includes(toSectionGroupKey(sectionGroup))
      ) {
        onCreateSectionGroup(
          selectedSectionIds,
          sectionGroup.sectionGroupAreaName,
          sectionGroup.sectionGroupColor
        );
        setOpen(false);
      }
    },
    [existingGroupIds, onCreateSectionGroup, selectedSectionIds]
  );

  const canUnassignSections = useMemo(() => {
    return selectedSectionIds.some((id) => zonedSectionIds.has(id));
  }, [selectedSectionIds, zonedSectionIds]);

  return (
    <Stack direction="row" gap="xxl" alignItems="center">
      <MultiSectionsDisplay sections={selectedSections} />
      <Stack direction="row" gap="s" alignItems="center">
        {canUnassignSections && (
          <Button
            size="md"
            variant={isDarkMode ? 'outline' : 'outlineGray'}
            textColor={'inverted'}
            style={{ background: 'transparent' }}
            onClick={() => onUnassignSections(selectedSectionIds)}
          >
            <Content id={ContentId.UnassignSections} />
          </Button>
        )}

        {!isEmpty(sectionGroups) && (
          <Button
            size="md"
            variant={isDarkMode ? 'outline' : 'outlineGray'}
            textColor={'inverted'}
            style={{ background: 'transparent' }}
            onClick={onUpdateZoneGroup}
          >
            <Content id={ContentId.EditZone} />
          </Button>
        )}

        <ClickToExpand
          id={ContentId.AddZoneGroup}
          open={open}
          location="top"
          setOpen={setOpen}
          trigger={
            <Button
              size="md"
              style={{ background: 'transparent' }}
              variant={isDarkMode ? 'outline' : 'outlineGray'}
              textColor={'inverted'}
            >
              <Content id={ContentId.AddZoneGroup} />
            </Button>
          }
          titleContentId={ContentId.AddZoneGroup}
          content={
            <ModifySectionGroup
              sectionGroup={DefaultSectionGroup}
              selectedSections={selectedSections}
              onSaveSectionGroup={onSaveSectionGroup}
              existingGroupIds={sectionGroups.map(({ id }) => id)}
            />
          }
        />

        <CrossIcon
          withHoverEffect
          fill={vars.color.textInverted}
          onClick={onResetSections}
        />
      </Stack>
    </Stack>
  );
};
